// packages/web-ui/src/state/tutorial.ts
//
// Tutorial slice. Owns:
//   - the login-time "show / skip / don't show again" prompt dialog
//   - the react-joyride spotlight tour state (running / step index)
//
// Persistence model:
//   `localStorage['tessa.tutorial.dismissed.<userId>'] = '1'` — set when the
//   user ticks "Don't show this again" before clicking Show or Skip. The flag
//   is per-user (keyed by AuthMe.id) so a shared browser doesn't carry one
//   user's choice into another's session.
//
// Session-scope (in-memory only — resets on full page reload):
//   `hasOfferedThisSession` so navigation/hash-changes that re-mount the gate
//   don't re-open the dialog after the user already chose this session.

import { create } from 'zustand';

export const TUTORIAL_DISMISS_KEY_PREFIX = 'tessa.tutorial.dismissed.';

export function dismissKey(userId: string): string {
  return `${TUTORIAL_DISMISS_KEY_PREFIX}${userId}`;
}

export function readDismissedForever(userId: string): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(dismissKey(userId)) === '1';
}

export function writeDismissedForever(userId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(dismissKey(userId), '1');
}

export function clearDismissedForever(userId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(dismissKey(userId));
}

interface TutorialState {
  /** True while the offer dialog is mounted. */
  promptOpen: boolean;
  /** True after the dialog has been shown (any choice) this browser session. */
  hasOfferedThisSession: boolean;
  /** True while the Joyride tour is running. */
  tourRunning: boolean;
  /** Active step index in the tour (0-based). */
  currentStep: number;

  /**
   * Called on login mount. Opens the prompt unless the user has dismissed
   * forever OR the prompt was already offered this session.
   */
  offerIfNeeded: (userId: string) => void;

  /**
   * Open the prompt unconditionally. Used by the Help menu's "Start tutorial"
   * item — when the user explicitly asks, we always show. (Help-menu also has
   * the option to call `startTour()` directly to skip the offer dialog.)
   */
  forcePrompt: () => void;

  /** User clicked "Show tutorial". Persists the dismiss flag if requested. */
  acceptOffer: (userId: string, dontShowAgain: boolean) => void;
  /** User clicked "Skip for now". Persists the dismiss flag if requested. */
  declineOffer: (userId: string, dontShowAgain: boolean) => void;

  /** Start the Joyride tour from step 0 (bypasses the prompt). */
  startTour: () => void;
  /** Stop the tour (user closed, finished, or skipped). */
  stopTour: () => void;
  /** Update the current step. */
  setStep: (idx: number) => void;
}

export const useTutorialStore = create<TutorialState>((set, get) => ({
  promptOpen: false,
  hasOfferedThisSession: false,
  tourRunning: false,
  currentStep: 0,

  offerIfNeeded: (userId) => {
    if (get().hasOfferedThisSession) return;
    if (readDismissedForever(userId)) {
      set({ hasOfferedThisSession: true });
      return;
    }
    set({ promptOpen: true, hasOfferedThisSession: true });
  },

  forcePrompt: () => {
    set({ promptOpen: true });
  },

  acceptOffer: (userId, dontShowAgain) => {
    if (dontShowAgain) writeDismissedForever(userId);
    set({ promptOpen: false, tourRunning: true, currentStep: 0 });
  },

  declineOffer: (userId, dontShowAgain) => {
    if (dontShowAgain) writeDismissedForever(userId);
    set({ promptOpen: false });
  },

  startTour: () => {
    set({ promptOpen: false, tourRunning: true, currentStep: 0 });
  },

  stopTour: () => {
    set({ tourRunning: false, currentStep: 0 });
  },

  setStep: (idx) => {
    set({ currentStep: idx });
  },
}));
